import _get from 'lodash/get';
import _set from 'lodash/set';
import { filterPrimaryKeys, isPresent } from '@/lib/utils';

const isAutocomplete = (propertyMeta) => {
  if (!propertyMeta) return;
  return propertyMeta.dataprovider || propertyMeta.deferredLookup || propertyMeta._customDataProviderObject || propertyMeta.items;
};


/**
 * Build sorted classname picker items from loaded class schemas.
 * Shape used by Query Builder: [{ _ui_label, name }, ...]
 * Includes queryable classes only (missing table_meta.queryable counts as true).
 * Order is queryable and included; CustomerOrder is not.
 * @param {Object} schema - meta schema map (classname -> class meta)
 * @return {Object[]}
 */
const schemaClassItems = (schema = {}) => {
  const byName = new Map();
  for (const key of Object.keys(schema)) {
    if (!_get(schema, `${key}.table_meta.queryable`, true)) continue;
    const name = _get(schema[key], 'classname') || key;
    if (!name || byName.has(name)) continue;
    byName.set(name, {
      _ui_label: _get(schema[key], 'labels.singular') ||
        _get(schema[key], 'labels.plural') ||
        name,
      name,
    });
  }
  return [...byName.values()].sort((a, b) => {
    const labelA = (a._ui_label || '').toLowerCase();
    const labelB = (b._ui_label || '').toLowerCase();
    if (labelA < labelB) return -1;
    if (labelA > labelB) return 1;
    return 0;
  });
};


/**
 * Class list for ClassLink classname autocomplete.
 * Starts from schemaClassItems (so Order is included once) and maps the
 * value key to `classname` to match the ClassLink model field.
 * Also drops any leftover entry that displays as "Order"/"Orders" but is
 * not classname Order (CustomerOrder shares that label).
 * @param {Object} schema - meta schema map
 * @return {Object[]} [{ _ui_label, classname }, ...]
 */
const classLinkClassItems = (schema = {}) => {
  return schemaClassItems(schema)
    .filter(({ name, _ui_label: label }) => {
      if (
        name === 'CustomerOrder' ||
        name.endsWith('Audit') ||
        name.endsWith('Archive') ||
        name.startsWith('Interface') ||
        ((label === 'Order' || label === 'Orders') && name !== 'Order')
      ) {
        return false;
      }
      return true;
    })
    .map(({ _ui_label, name }) => ({ _ui_label, classname: name }));
};


/**
 * Recursive lookup of a field value on related parent/child objects.
 * Used by visibleonlyif evaluation for dotted paths like order.type.
 * @param {Object} params
 * @param {Object} params.record - root record
 * @param {String} params.className - classname from mapped relationship
 * @param {String} params.property - relationship property name
 * @param {String} params.field - field to read
 * @param {String} [params.parentPath=''] - path relative to record
 * @param {Boolean} [params.child=false] - traversing downward; do not walk up
 * @return {*}
 */
const findReferencedValue = ({
  record,
  className,
  property,
  field,
  parentPath = '',
  child = false,
}) => {
  const model = _get(record, parentPath, record);
  if (!model) return null;

  // Matching classnames means we're in the closest matching target class
  if (property) {
    const modelClassName = model._classname || '';
    if (modelClassName === className || modelClassName.toLowerCase() === property) {
      return model[field];
    }
  }

  // Looks for properties with names based off their child's classname
  const propertyObject = model[className];
  const hasPropertyObject = propertyObject && typeof propertyObject === 'object';
  if (hasPropertyObject && !(propertyObject instanceof Array)) {
    return propertyObject[field];
  }

  // Check for the value in child objects
  let value = null;
  const keys = Object.keys(model);
  for (const key of keys) {
    if (value) break;
    if (typeof model[key] === 'object' && !(model[key] instanceof Array)) {
      const childParentPath = `${parentPath}${parentPath ? '.' : ''}${key}`;
      value = findReferencedValue({
        record,
        className,
        property,
        field,
        parentPath: childParentPath,
        child: true,
      });
    }
  }

  if (child) return value;

  const splitPath = parentPath.split('.');
  const newParentPath = splitPath.splice(0, splitPath.length - 1).join('.');
  if (!newParentPath) return value;

  // Move up to the parent if still unresolved
  if (!isPresent(value) && newParentPath.length) {
    value = findReferencedValue({
      record,
      className,
      property,
      field,
      parentPath: newParentPath,
    });
  }
  return value;
};


/**
 * Evaluate a visibleonlyif condition string against a model.
 * FORMAT: 'order.type:SAL,RES;productid:THIS,THAT'
 * Blank / null / empty => always visible (true).
 * @param {String} visibleonlyif
 * @param {Object} context
 * @param {Object} context.model - local model for flat field checks
 * @param {Object} [context.record] - root record for dotted-path lookup (defaults to model)
 * @param {String} [context.parentPath=''] - path of model within record
 * @param {Object} [context.classMeta={}] - class meta for relationship className resolution
 * @return {Boolean}
 */
const evaluateVisibleOnlyIf = (
  visibleonlyif,
  {
    model = {},
    record = model,
    parentPath = '',
    classMeta = {},
  } = {},
) => {
  if (!visibleonlyif) return true;
  const conds = visibleonlyif.split(';');

  for (const cond of conds) {
    const splitCond = cond.split(':');
    const testValues = splitCond[1].split(',');
    const fieldToCheck = splitCond[0];
    const pathToField = fieldToCheck.split('.');

    let value = null;
    if (pathToField.length > 1) {
      const property = pathToField[0];
      const field = pathToField[1];
      const className = _get(classMeta, `properties.${property}.className`, '');
      value = findReferencedValue({
        record,
        className,
        property,
        field,
        parentPath,
      });
    } else {
      value = model[fieldToCheck];
    }
    if (!isPresent(value)) return false;
    if (testValues[0] === '*' && testValues.length === 1) continue;
    if (!testValues.includes(value)) return false;
  }
  return true;
};


/**
 * Determines appropriate RInput component type based on prop object
 * @param {Object} [prop={}] meta property object
 * @return {string} RInput component name
 */
const determineRInputType = (prop = {}, className, fieldName) => {
  const isValidDeliveryCalendarInput = (cls, field) => {
    const fieldsByClass = {
      Order: ['deliverydate'],
      Transfer: ['deliverydate'],
    };
    return fieldsByClass[cls] && fieldsByClass[cls].includes(field);
  };
  const propertyMeta = _get(prop, 'property_meta', {});
  const format = _get(prop, 'format');
  const type = _get(prop, 'type');
  if (propertyMeta.flag) return 'RCheckbox';
  if (prop.enum) return 'RSelect';
  if (format && format === 'date' && isValidDeliveryCalendarInput(className, fieldName)) return 'RDeliveryCalendarInput';
  if ((format && format.includes('date')) || type === 'date-time') return 'RDatePicker';
  if ((format && format === 'time') || type === 'time') return 'RTimePicker';
  if (propertyMeta.useComboBox) return 'RCombobox';
  if (isAutocomplete(propertyMeta)) return 'RAutocomplete';
  if (propertyMeta._codeeditor) return 'RCodeEditor';
  if (propertyMeta.allownewline || propertyMeta.textheight) return 'RTextArea';
  return 'RTextField';
};


const setAdditionalRowData = (defaultMeta, additionalParams) => {
  if (additionalParams.flag) defaultMeta.property_meta.flag = additionalParams.flag;
  if (additionalParams.enum) defaultMeta.property_meta.enum = additionalParams.enum;
  if (additionalParams.dataprovider) defaultMeta.property_meta.dataprovider = additionalParams.dataprovider;
  if (additionalParams._lookupValue) defaultMeta.property_meta._lookupValue = additionalParams._lookupValue;
  if (additionalParams.field_auth_reason) defaultMeta.property_meta.field_auth_reason = additionalParams.field_auth_reason;
  if (additionalParams.deferredLookup) defaultMeta.property_meta.deferredLookup = additionalParams.deferredLookup;
  if (additionalParams.textheight) defaultMeta.property_meta.textheight = additionalParams.textheight;
  if (additionalParams.textwidth) defaultMeta.property_meta.textwidth = additionalParams.textwidth;
  if (additionalParams.format) defaultMeta.format = additionalParams.format;
  if (additionalParams.readonly) defaultMeta.property_meta.readonly = additionalParams.readonly;
  if (additionalParams._disabled) defaultMeta.property_meta._disabled = additionalParams._disabled;
  if (additionalParams.datatype) defaultMeta.property_meta.datatype = additionalParams.datatype;
  if (additionalParams._problemNames) defaultMeta.property_meta._problemNames = additionalParams._problemNames;
  if (additionalParams.enumValues) defaultMeta.labels.enumvalues = additionalParams.enumValues;
  if (isPresent(additionalParams._min)) defaultMeta.property_meta._min = additionalParams._min;
};

/**
 * Creates a simple meta row with default values. Useful for when
 * meta for a given field needs to be present in the return data but
 * does not exist on the meta provided.
 * @param {string} label
 * @param {string} dataType
 * @param {Object} [additionalParams={}] - set additional params to coerce Rinput type
 * @return {Object} meta row object
 */
const createMetaRow = (label, dataType = 'string', additionalParams = {}) => {
  const defaultMeta = {
    defaultquery: 'CI',
    labels: { label },
    property_meta: { source: 'db_schema' },
    queryable: true,
    type: dataType,
    // _isCustom: to flag that this is created client side:
    // probably want to REFACTOR to use different source, instead of
    // 'db_schema', such as 'js_hard_coded'
    _isCustom: true,
  };
  setAdditionalRowData(defaultMeta, additionalParams);
  return defaultMeta;
};

/**
 * Inserts the passed meta property after the specified "anchor"
 * property without assuming which tab it lives in (since that can be
 * dynamically changed in retailer rules).
 * If replace is true then the property replaces the anchor.
 * @param {Object} classMeta (caller must have already cloned)
 * @param {Object} propName
 * @param {Object} prop
 * @param {string} anchor
 * @param {Boolean} replace whether to replace instead of insert
 */
const metaInsert = ({ classMeta, propName, prop, anchor, replace = false }) => {
  classMeta.properties[propName] = prop;
  // find anchor
  let tab;
  for (tab of classMeta.display) {
    const ind = tab.displayfields.indexOf(anchor);
    if (ind > -1) {
      tab.displayfields.splice(
        replace ? ind : ind + 1,
        replace ? 1 : 0,
        propName,
      );
      return;
    }
  }
  // we never found the anchor.  Just append the new prop
  if (tab) {
    tab.displayfields.splice(tab.displayfields.length, 0, propName);
  }
};

/**
 * Fetches all displayfields from a meta, or from a specific tab
 * @param {Object} meta - meta to pull displayfields from
 * @param {String} tabName - optional tab name to pull displayfields from
 * @returns {Array} - array of displayfields
 */
const getDisplayFields = (meta, tab) => {
  if (!meta || !meta.display) return null;
  if (tab) return _get(meta.display.find((t) => t.tabname === tab), 'displayfields');
  return meta.display.reduce((acc, t) => {
    acc = [...acc, ...t.displayfields];
    return acc;
  }, []);
};


/**
 * Return either the tab in which propName is found or null if not visible.
 */
const findTab = (meta, propName) => {
  for (const tab of meta.display) {
    if (tab.displayfields.includes(propName)) return tab;
  }
  return null;
};


// REFACTOR: this could go away with #11238 - unless other use is found
const derivedMetaRow = (meta, derivedFrom) => {
  const [label, dataType = 'string', additionalParams = {}] = meta;
  const defaultMeta = createMetaRow(label, dataType, additionalParams);
  defaultMeta.property_meta = { ...defaultMeta.property_meta, ...derivedFrom };
  // must reset additionaParams that are overwritten by derivedFrom.
  setAdditionalRowData(defaultMeta, additionalParams);
  return defaultMeta;
};

/**
 * Filters state object by source type
 * @param {Object} state
 * @param {String[]} source_string_array
 * @return {Object[]} Filtered state by source
 */
const filterDbProperties = (state, source_string_array, options) => {
  const { advSearch = false, sortAlphabetically = false, additionalFields = [], returnObject = false } = options;
  const props = returnObject ? {} : [];

  Object.keys(state.properties).forEach((key) => {
    if (!source_string_array.includes(_get(state, `properties[${key}]property_meta.source`))) return;
    if (advSearch && !_get(state, `properties[${key}].queryable`)) return;
    const label = _get(state, `properties[${key}].labels.label`, '');
    const p = {
      className: state.classname,
      name: key,
      data: state.properties[key],
      label,
    };
    if (returnObject) props[key] = p;
    else props.push(p);
  });


  if (additionalFields.length) props.push(...additionalFields);
  if (returnObject) return props;
  const alphaSort = (a, b) => {
    if (a.label.toLowerCase() < b.label.toLowerCase()) return -1;
    if (a.label.toLowerCase() > b.label.toLowerCase()) return 1;
    return 0;
  };

  let sortFunc;
  if (sortAlphabetically || advSearch) {
    sortFunc = alphaSort;
  }
  // normally should sort by display order:
  else if (state.display && state.display.length) {
    const allDisplayFields = state.display.reduce((acc, tab) => {
      return [...acc, ...tab.displayfields];
    }, []);
    sortFunc = (a, b) => {
      if (a === b) return 0;
      const aInd = allDisplayFields.indexOf(a.name);
      const bInd = allDisplayFields.indexOf(b.name);
      if (aInd >= 0 && bInd >= 0) return aInd - bInd;
      if (aInd >= 0) return -1;
      if (bInd >= 0) return 1;
      // neither are in display, fallback on alphabetical
      return alphaSort(a, b);
    };
  }
  // fallback: put PK first
  else {
    const keys = filterPrimaryKeys(state);
    sortFunc = (prop1, prop2) => {
      if (keys.indexOf(prop1.name) >= 0) return -1;
      if (keys.indexOf(prop2.name) >= 0) return 1;
      return alphaSort(prop1, prop2);
    };
  }

  props.sort(sortFunc);
  return props;
};


const addParentRefs = (localModel, model, parentPath) => {
  if (!parentPath) return;
  let relativePath = '';
  if (parentPath.includes('record.')) {
    const splitPath = parentPath.split('.');
    relativePath = splitPath.slice(1);
  } else relativePath = parentPath.split('.');
  if (relativePath.length < 2) return;
  localModel._parent = _get(model, relativePath.join('.'));
  for (let i = relativePath.length - 1; i > 0; i -= 1) {
    const path = relativePath.slice(0, i).join('.');
    const parentRef = _get(model, path);
    if (!parentRef) return;
    if (parentRef._parent) continue;
    const childPath = relativePath.slice(0, i + 1).join('.');
    _set(model, `${childPath}._parent`, parentRef);
  }
};


export {
  determineRInputType,
  createMetaRow,
  derivedMetaRow,
  filterDbProperties,
  addParentRefs,
  metaInsert,
  getDisplayFields,
  findTab,
  isAutocomplete,
  schemaClassItems,
  classLinkClassItems,
  findReferencedValue,
  evaluateVisibleOnlyIf,
};
